import { ReactElement, useContext, useEffect } from "react";
import { useLocation, matchRoutes } from "react-router-dom";
import Header from "../Header/Header";
import LandingHeader from "../Header/LandingHeader";
import LandingMenu from "../Header/LandingMenu";
import SideMenu from "../Navigation/SideMenu";
import Main from "../../../Main";
import ScrollUpFloatingButton from "../../FloatingButton/ScrollUpFloatingButton";
import { memberLegacyPaths } from "../../../routes/MemberLegacyRoutes";
import { staffLegacyPaths } from "../../../routes/StaffLegacyRoutes";
import {
  publicLegacyPaths,
  events_legacy_paths,
} from "../../../routes/PublicLegacyRoutes";
import { adminLegacyPaths } from "../../../routes/AdminLegacyRoutes";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import AuthContext from "../../../store/AuthContext";
import { routes } from "../../../routes";
import { ToastContainer } from "react-toastify";
import defaultBannerPattern from "../../../assets/images&icons/banner-pattern.png";
import TitleLayout from "./TitleLayout";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import { isAuthLayoutRoute } from "../../../utils/authLayoutRoutes";
import { ADVANCED_MEMBER_SEARCH_PATH } from "../../../utils/memberSearchNavigation";

export interface IRouteHandle {
  breadcrumbRightActions?: React.ComponentType<object>;
  bannerPatternImage?: string;
}

interface IGeneralLayoutProps {
  children?: ReactElement;
}

function GeneralLayout({ children }: IGeneralLayoutProps): ReactElement {
  const location = useLocation();
  const { loggedPerson, isAuthenticated, isAuthTransitionActive, setAuthTransitionActive } = useContext(AuthContext);

  const publicRoutes = [
    "/aboutus",
    "/events/*",
    "/benefits",
    "/objectives",
    "/join",
    "/board",
    "/councils",
    "/email-subscriptions",
    "/unsubscribed",
  ]

  const checkPublicRouteMatch = (pathname: string) => {
    const isPublicRoute = publicRoutes.some((route) => {
      return pathname === route || pathname.startsWith(route + "/");
    });
    return isPublicRoute;
  };

  const simpleLayoutRoutes = [
    ...memberLegacyPaths,
    ...staffLegacyPaths,
    ...publicLegacyPaths,
    ...events_legacy_paths,
    ...adminLegacyPaths,
    "/",
    "/login.asp",
    "/two-factor-acknowledgement",
    "/two-factor-auth",
  ];

  const checkRouteMatch = (pathname: string) => {
    return simpleLayoutRoutes.some((route) => {
      const routeLower = route.toLowerCase();

      if (routeLower.includes("*")) {
        const routePattern = routeLower.replace("*", "");
        return pathname.startsWith(routePattern);
      }
      return pathname === routeLower || pathname.startsWith(routeLower + "/");
    });
  };

  const pathname = location.pathname.toLowerCase();
  const matchesSimpleLayoutRoute = checkRouteMatch(pathname);
  const hideHeaderAndSideMenu = pathname === "/" || pathname === "/landing-page-client" || pathname === "/landing-page-ssr";
  const onAuthLayoutRoute = isAuthLayoutRoute(pathname);

  useEffect(() => {
    if (!onAuthLayoutRoute && isAuthTransitionActive) {
      setAuthTransitionActive(false);
    }
  }, [onAuthLayoutRoute, isAuthTransitionActive, setAuthTransitionActive]);

  const shouldShowLandingHeader =
    (!isAuthenticated && !hideHeaderAndSideMenu) ||
    onAuthLayoutRoute ||
    isAuthTransitionActive;

  const matchedRoutes = matchRoutes(routes, location);
  const isNotFoundRoute =
    matchedRoutes &&
    matchedRoutes.length > 0 &&
    matchedRoutes[matchedRoutes.length - 1]?.route.path === "*";

  const leafRoute = matchedRoutes?.length ? matchedRoutes[matchedRoutes.length - 1]?.route : null;
  const routeHandle = leafRoute?.handle as IRouteHandle | undefined;
  const BreadcrumbRightActionsComponent = routeHandle?.breadcrumbRightActions;
  const breadcrumbRightActions = BreadcrumbRightActionsComponent ? <BreadcrumbRightActionsComponent /> : undefined;
  const bannerPatternImage = routeHandle?.bannerPatternImage ?? defaultBannerPattern;

  const isSimpleLayout = matchesSimpleLayoutRoute || isNotFoundRoute;
  const { isStaffMode } = useStaffMode();
  const isAdvancedMemberSearchPage =
    pathname === ADVANCED_MEMBER_SEARCH_PATH.toLowerCase();

  return (
    <div
      className={`relative h-screen overflow-hidden w-full flex flex-col ${
        isAdvancedMemberSearchPage ? "bg-[#f6f6f6]" : "bg-gray-100"
      }`}
    >
      {shouldShowLandingHeader ? (
        <>
          <LandingHeader forceActive />
          {!isAuthenticated && !isAuthTransitionActive && <LandingMenu />}
        </>
      ) : (
        <>
          {!hideHeaderAndSideMenu && <Header />}
          {!hideHeaderAndSideMenu && <SideMenu />}
        </>
      )}
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={true}
        draggable={true}
        pauseOnHover={true}
        theme="light"
      />

      {isSimpleLayout ? (
        <div className={`${checkPublicRouteMatch(pathname) && 'px-12'} flex-1 flex flex-col min-h-0 relative overflow-y-auto ${shouldShowLandingHeader ? 'pt-[88px]' : ''}`}>
          {children}
        </div>
      ) : (
        <>
          {shouldShowLandingHeader && <div className="pt-[88px]" />}
          <Main>
            <div className={`flex flex-col gap-2 ${isStaffMode && 'pb-4'}`}>
              <TitleLayout
                bannerPatternImage={bannerPatternImage ?? ''}
              />
              <Breadcrumb
                loggedPerson={loggedPerson}
                rightActions={breadcrumbRightActions}
              />

            </div>
            {children}
          </Main>
        </>
      )}

      <ScrollUpFloatingButton />
    </div>
  );
}

export default GeneralLayout;
